export enum UserRole {
  Administrator = "Administrator",
  Organization = "Organization",
}

export interface ErrorResponse {
  error: number;
  message: string;
}
export interface EmptyResponse {
  message?: string;
  ok: boolean;
}
export interface SuccessResponse<T> {
  data: T;
  message?: string;
}
export interface TokenResponse {
  role: string | null;
  expired: boolean;
  sub: string | null;
}

export interface JwtPayloadTransformer {
  exp?: number;
  [claim: string]: any;
}
export interface User {
  id: string;
  fullName: string;
  email: string;
  role: "Administrator" | "Organization";
  organizationId?: string;
  organization?: string;
  status: number;
  createdAt: string;
  lastAccessAt?: string;
  password?: string;
  roleId?: string; // Optional, used for role management
}

export interface Entity {
  contactEmail?: string;
  id: string;
  name: string;
  description: string;
  email: string;
  phone?: string;
  address?: string;
  status: number;
  createdAt: string;
  userCount: number;
}

export interface Survey {
  id: string;
  title: { es: string; ca: string };
  description?: { es: string; ca: string };
  type: "generic" | "entity";
  organizationId?: string;
  organizationName?: string;
  defaultLanguage: "es" | "ca" | "en" | "ar";
  questions: Question[];
  createdAt: string;
  updatedAt: string;
  orientation: "niño" | "familia";
  objetivo: number;
  status: "draft" | "published" | "archived";
  code?: string;
  IsCodeActive?: boolean;
}
export type QuestionsMode = { kind: "generic" } | { kind: "specific" };

export interface QuestionFormData
  extends Omit<Question, "id" | "text" | "createdAt"> {
  text_es: string;
  text_ca: string;
  text_en?: string;
  text_ar?: string;
  audioEnabled: boolean;
  audioFiles?: {
    es?: File | null;
    ca?: File | null;
    en?: File | null;
    ar?: File | null;
  };
  audioUrls?: { es?: string; ca?: string; en?: string; ar?: string };
  audioObsKeys?: { es?: string; ca?: string; en?: string; ar?: string };
  organizationId?: string;
}
export enum QuestionType {
  Generic = 0,
  Custom = 1,
}

export const QuestionTypeLabelMap: Record<QuestionType, string> = {
  [QuestionType.Generic]: "Genérica",
  [QuestionType.Custom]: "Personalizada",
};

export interface Question {
  id: string;
  text: { es: string; ca: string; en?: string; ar?: string };
  audioUrl?: string;
  audioUrls?: { es?: string; ca?: string; en?: string; ar?: string };
  audioObsKeys?: { es?: string; ca?: string; en?: string; ar?: string };
  type: QuestionType;
  order?: number;
  isGeneric: boolean;
  surveyId?: string;
  createdAt?: string;
  audioFiles?: {
    es?: File | null;
    ca?: File | null;
    en?: File | null;
    ar?: File | null;
  };
  organizationId?: string;
  organizationName?: string;
}

export interface AccessCode {
  id: string;
  surveyId: string;
  surveyTitle: string;
  code: string;
  isActive: boolean;
  createdAt: string;
  organizationId?: string;
  organizationName?: string;
  questionCount?: number;
  type?: number; // 1 = entity (específica), 2 = generic (genérica)
}

export interface Response {
  id: string;
  surveyId: string;
  type: "child" | "family";
  answers: Record<string, number>;
  language: "es" | "ca" | "en" | "ar";
  createdAt: string;
}

export interface DashboardMetrics {
  totalSurveys: number;
  activeEntities?: number;
  totalResponses: number;
  activeCodes: number;
  inactiveCodes: number;
  surveyTrendPercentage?: number;
  entityTrendPercentage?: number;
  responseTrendPercentage?: number;
  codeTrendPercentage?: number;
  // Number of surveys that have reached their target/goal
  surveysReachedTarget?: number;
  // Percentage of surveys that reached their target
  surveysReachedTargetPercentage?: number;
  // Target progress per survey for the new section
  surveyTargets?: Array<{
    title: string;
    type: 1 | 2; // 1 = Entidad, 2 = Generica
    organizationName: string | null;
    targetRequired: number;
    completed: number;
  }>;
  // New KPIs
  questionsWithAudio?: number;
  questionsWithoutAudio?: number;
  genericSurveys?: number;
  specificSurveys?: number;
  ownSurveys?: number;
  ownResponses?: number;
  ownCodeStatus?: boolean;
  lastPublished?: string;
}

export interface Activity {
  id: string;
  type:
    | "survey_created"
    | "survey_published"
    | "code_activated"
    | "data_exported";
  title: string;
  description: string;
  timestamp: string;
  user: string;
  entityName: string;
}

export type Language = "es" | "ca";
export type RoleEntity = {
  name: "Administrator" | "Organization";
  id: string;
};

export interface SurveyQuestionAssignment {
  questionId: string;
  order: number;
}

export type SurveyWriteBase = Omit<
  Survey,
  "id" | "createdAt" | "updatedAt" | "organizationName" | "questions"
> & {
  questions?: SurveyQuestionAssignment[];
};

export type UpdateSurveyPayload = SurveyWriteBase & { id: string };

export type ExportableSurvey = {
  id: string;
  title: string;
  responses: number;
  lastSubmissionAt?: string | null;
};
export type ExportHistoryItem = {
  id: string;
  surveyId: string;
  surveyTitle: string;
  requestedAtUtc: string;
  language: "es" | "ca" | "en";
  respondentType: "" | "child" | "family";
  startDateUtc?: string | null;
  endDateUtc?: string | null;
  rows: number;
};
export interface GeneratedAudio {
  blob: Blob;
  url: string;
  file: File;
}
export type Lang = "es" | "ca" | "en" | "ar";
export const EMPTY_AUDIO_FILES: Record<Lang, File | null> = {
  es: null,
  ca: null,
  en: null,
  ar: null,
};
export type GenerateWithSaveResponse = {
  objectKey: string;
  file: { fileName: string; contentType: string; base64: string };
};
